import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, BadgeCheck, CheckCircle2, Wallet, Star, Camera, Scale,
  AlertTriangle, ShieldAlert, Check,
} from "lucide-react";

const SECTIONS = [
  {
    icon: BadgeCheck, title: "Identity Verification",
    body: "To help promote accountability within our community, renters may be required to verify their identity before renting items. Verification may include:",
    list: ["Government-issued photo identification", "Verified email address", "Verified mobile phone number"],
    footer: "Identity verification helps create a safer marketplace and discourages fraudulent activity.",
  },
  {
    icon: CheckCircle2, title: "Booking Approval Control",
    body: "Owners remain in control of who rents their items. Every booking request must be reviewed by the owner before it is confirmed. Owners can:",
    list: ["Accept booking requests", "Reject booking requests", "Review renter profiles before accepting"],
    footer: "You are never required to rent your item to someone you do not feel comfortable renting to.",
  },
  {
    icon: Wallet, title: "Security Deposits",
    body: "Owners may choose to require a security deposit for their listings. Security deposits may help cover:",
    list: ["Damage to items", "Missing components", "Excessive wear and tear", "Non-return of an item"],
    footer: "Owners are responsible for selecting an appropriate deposit amount based on the value and nature of their item.",
  },
  {
    icon: Star, title: "Ratings and Reviews",
    body: "Our review system helps build trust and accountability. Before accepting a booking, owners can review a user's:",
    list: ["Ratings", "Reviews", "Transaction history", "Verification status"],
    footer: "Users who consistently receive poor reviews or violate marketplace policies may have their accounts restricted or removed.",
  },
  {
    icon: Camera, title: "Before and After Photos",
    body: "Owners are strongly encouraged to document item condition with photos.",
    twoCol: {
      left: { title: "Before Handover", intro: "Take clear photographs showing:", items: ["Overall item condition", "Existing marks or wear", "Accessories and components included"] },
      right: { title: "After Return", intro: "Take photographs immediately upon inspection of the returned item.", items: ["Photographic evidence may assist in resolving disputes."] },
    },
  },
  {
    icon: Scale, title: "Dispute Reporting",
    body: "If damage, loss, theft, or non-return occurs, owners may submit a dispute through SelLend. Supporting evidence may include:",
    list: ["Photographs", "Booking information", "Communication records", "Proof of item condition"],
    footer: "SelLend may review submitted information and take appropriate platform action where necessary.",
  },
  {
    icon: AlertTriangle, title: "Non-Return Reporting",
    body: "If an item is not returned as agreed:",
    ordered: ["Report the issue through SelLend immediately.", "Provide relevant details and evidence.", "Allow SelLend to investigate the matter."],
    footer: "Repeated non-return incidents may result in account suspension or permanent removal from the platform.",
  },
  {
    icon: ShieldAlert, title: "Fraud Prevention",
    body: "To help maintain trust within the marketplace, SelLend reserves the right to:",
    list: ["Verify user identities", "Request additional documentation", "Review suspicious transactions", "Limit account access", "Suspend or remove users who violate platform policies"],
  },
];

export default function OwnerProtection() {
  return (
    <div data-testid="owner-protection-page" className="min-h-screen">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">
            <ShieldCheck className="w-4 h-4" /> Owner Protection Program
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.95]">Owner Protection Guidelines</h1>
          <div className="mt-8 bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-2xl p-6">
            <p className="text-muted-foreground text-sm leading-relaxed">
              At SelLend, we understand that lending or renting out your belongings requires trust. While no marketplace can eliminate all risk, we have developed a range of features and processes designed to help owners make informed decisions and reduce potential risks.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 space-y-8">
        {SECTIONS.map((s, i) => (
          <motion.div
            key={s.title} data-testid={`owner-section-${i}`}
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
            className="bg-white border border-black/10 rounded-2xl p-6 sm:p-7"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#FFF0E8] flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5 text-[#FF5A00]" />
              </div>
              <h2 className="font-display text-xl font-bold tracking-tight">{s.title}</h2>
            </div>
            {s.body && <p className="text-muted-foreground text-sm mt-4 leading-relaxed">{s.body}</p>}

            {s.list && (
              <ul className="space-y-2 mt-4">
                {s.list.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#E3F0E3] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-[#2E7D32]" /></span>
                    {d}
                  </li>
                ))}
              </ul>
            )}

            {s.ordered && (
              <ol className="space-y-2.5 mt-4">
                {s.ordered.map((d, n) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#0A0A0A] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{n + 1}</span>
                    {d}
                  </li>
                ))}
              </ol>
            )}

            {s.twoCol && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                {[s.twoCol.left, s.twoCol.right].map((col) => (
                  <div key={col.title} className="bg-[#F7F5F1] border border-black/5 rounded-xl p-4">
                    <h3 className="font-semibold text-sm">{col.title}</h3>
                    <p className="text-muted-foreground text-xs mt-1.5 leading-relaxed">{col.intro}</p>
                    <ul className="space-y-1.5 mt-3">
                      {col.items.map((d) => (
                        <li key={d} className="flex items-start gap-2 text-sm leading-relaxed">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF5A00] shrink-0" />{d}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {s.footer && <p className="text-sm mt-4 leading-relaxed font-medium">{s.footer}</p>}
          </motion.div>
        ))}

        <div className="bg-[#0A0A0A] text-white rounded-2xl p-6">
          <h3 className="font-semibold text-lg">Important Notice</h3>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            SelLend provides tools and processes designed to support safer transactions between users. However, SelLend is a marketplace platform and is not the owner, renter, lender, insurer, or guarantor of listed items. Users remain responsible for their own transactions and must exercise reasonable care when renting, lending, buying, or selling through the platform.
          </p>
        </div>

        <div className="bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-2xl p-6 text-center">
          <h3 className="font-display text-xl font-bold tracking-tight">Our Commitment</h3>
          <p className="text-muted-foreground text-sm mt-2 leading-relaxed max-w-xl mx-auto">
            Our goal is to create a trusted, transparent, and accountable marketplace where people can safely earn from the things they own and access the things they need.
          </p>
          <Link to="/create" data-testid="owner-cta-list" className="inline-flex items-center gap-1.5 mt-5 bg-[#0A0A0A] text-white rounded-full px-6 py-3 font-medium hover:bg-[#FF5A00] transition-colors active:scale-95">
            List an item
          </Link>
        </div>
      </section>
    </div>
  );
}
